import { useState } from 'react'
import PropTypes from 'prop-types'
import { X, Check, DownloadSimple } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import {
  transformMimeType,
  validateFileHeader,
  imageAllowedExtensions,
} from '../../../../helpers/fileValidation'
import { APP_COLORS } from '../../../../helpers/constants'

const FILE_MAX_SIZE = 3000000 // 3MB

const MediaUploadInput = ({ value, handleFieldChange, toggleImageDatabase, isError = false }) => {
  const { t } = useTranslation(['eventManagement'])
  const [error, setError] = useState(false)

  const validateFile = (file) => {
    handleFieldChange({ target: { name: 'photo', value: undefined } })
    if (file.size > FILE_MAX_SIZE) {
      return setError(true)
    }
    const fileType = transformMimeType(file.type)
    if (!imageAllowedExtensions.includes(fileType)) {
      return setError(true)
    }
    const reader = new FileReader()
    reader.onloadend = (e) => {
      if (e.target.readyState === FileReader.DONE) {
        const bytes = new Uint8Array(e.target.result)
        const isValid = validateFileHeader(bytes, fileType)
        if (isValid) {
          setError(false)
          handleFieldChange({ target: { name: 'image', value: file } })
        } else {
          setError(true)
        }
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateFile(e.dataTransfer.files[0])
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return
    validateFile(file)
  }

  return (
    <form id="file-upload" onDragEnter={handleDrag} onSubmit={(e) => e.preventDefault()}>
      <input hidden type="file" id="input-file-upload" onChange={handleFileChange} />
      <div
        className={`event-form-dropzone ${error || isError ? 'is-danger' : ''}`}
        id="drag-file-element"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {error && (
          <>
            <X color={APP_COLORS.red500} />
            <p className="error-message">{t('eventManagement:common.photoUploadError')}</p>
          </>
        )}
        {value ? (
          <>
            <Check color={APP_COLORS.green500} />
            <p className="success-message">
              {t('eventManagement:common.fileUploaded', {
                name: value.name ? value.name : value.id,
              })}
            </p>
          </>
        ) : (
          <>
            <DownloadSimple color="#000" />
            <p className="info-message">{t('eventManagement:common.dragAndDropImage')}</p>
            <div className="event-form-dropzone__divider-message">
              <div className="divider" />
              <span className="info-message">{t('eventManagement:common.or')}</span>
              <div className="divider" />
            </div>
          </>
        )}

        <div className="event-form-dropzone__buttons-container">
          <label htmlFor="input-file-upload" className="button button-gray">
            {t('eventManagement:common.uploadFromDevice')}
          </label>

          <button className="button button-gray" onClick={() => toggleImageDatabase(true)}>
            {t('eventManagement:common.browseLibrary')}
          </button>
        </div>
      </div>
    </form>
  )
}

MediaUploadInput.propTypes = {
  value: PropTypes.object,
  handleFieldChange: PropTypes.func.isRequired,
  toggleImageDatabase: PropTypes.func.isRequired,
  isError: PropTypes.bool,
}

export default MediaUploadInput
